<script setup>
/**
 * СТРАНИЦА ВИТРИНЫ (SHOWCASE) - NUXT 4 SSR READY
 */

const config = useRuntimeConfig();
const authToken = useCookie('authToken');

// 1. SSR ЗАПРОС ДАННЫХ
// Nuxt выполнит это на сервере, и данные будут в исходном коде (Ctrl+U)
const { data: projects, pending, error, refresh } = await useFetch('/showcase', {
  baseURL: config.public.apiBase,
  headers: {
    'Authorization': authToken.value ? `Bearer ${authToken.value}` : '',
    'Accept': 'application/json'
  },
  key: 'showcase-projects-list', // Уникальный ключ для синхронизации клиента и сервера
  transform: (res) => res.data || res
});

// Состояния для админки (работают только в браузере)
const isAdmin = computed(() => !!authToken.value);
const showModal = ref(false);
const isEditing = ref(false);
const editingId = ref(null);

const form = ref({
  title: '',
  description: '',
  project_link: '',
  cost: 0,
  image: null
});

// --- Методы управления ---

const openModal = () => {
  isEditing.value = false;
  form.value = { title: '', description: '', project_link: '', cost: 0, image: null };
  showModal.value = true;
};

const editProject = (project) => {
  isEditing.value = true;
  editingId.value = project.id;
  form.value = { ...project };
  showModal.value = true;
};

const handleFileChange = (e) => {
  form.value.image = e.target.files[0];
};

const submitForm = async () => {
  const formData = new FormData();
  Object.keys(form.value).forEach(key => {
    if (form.value[key] !== null) formData.append(key, form.value[key]);
  });

  try {
    const url = isEditing.value ? `/showcase/${editingId.value}` : '/showcase';
    await $fetch(url, {
      baseURL: config.public.apiBase,
      method: 'POST', // Laravel Update обычно идет через POST с имитацией или просто POST
      headers: { 'Authorization': `Bearer ${authToken.value}` },
      body: formData
    });
    showModal.value = false;
    await refresh(); // Перезагружаем SSR данные
  } catch (err) {
    alert('Ошибка при сохранении');
  }
};

const deleteProject = async (id) => {
  if (!confirm('Удалить проект?')) return;
  try {
    await $fetch(`/showcase/${id}`, {
      baseURL: config.public.apiBase,
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${authToken.value}` }
    });
    await refresh();
  } catch (err) {
    alert('Ошибка удаления');
  }
};
</script>

<template>
  <div class="min-h-screen bg-[#0F1113] text-gray-300 relative flex flex-col">

    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none fixed">
      <div class="absolute -top-[20%] left-[20%] w-[50%] h-[50%] bg-purple-900/10 rounded-full blur-[150px] animate-pulse-slow"></div>
      <div class="absolute top-[40%] right-[10%] w-[40%] h-[40%] bg-orange-900/10 rounded-full blur-[150px] animate-pulse-slow" style="animation-delay: 2s"></div>
    </div>

    <nav class="relative z-20 px-6 py-6 max-w-7xl mx-auto w-full flex justify-between items-center">
      <NuxtLink to="/" class="flex items-center gap-2 text-gray-400 hover:text-white transition group">
        <div class="w-8 h-8 rounded-lg bg-[#1A1C1E] border border-gray-700 group-hover:border-orange-500 flex items-center justify-center transition-colors">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        </div>
        <span class="text-sm font-medium">Вернуться на главную</span>
      </NuxtLink>

      <button v-if="isAdmin" @click="openModal" class="text-xs bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-bold transition shadow-lg shadow-orange-500/20">
        + Добавить проект
      </button>
    </nav>

    <main class="relative z-10 flex-grow px-4 sm:px-6 lg:px-8 pb-20">
      <div class="max-w-7xl mx-auto">

        <div class="text-center mb-16 animate-fade-in-up">
          <h1 class="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-gray-300 to-gray-500">
              Витрина решений
            </span>
          </h1>
          <p class="text-lg text-gray-500 max-w-2xl mx-auto">
            Библиотека реализованных проектов на стеке Gefest AI.
          </p>
        </div>

        <div v-if="error" class="text-center py-20 text-red-400">
          <p>Не удалось загрузить проекты. Попробуйте позже.</p>
        </div>

        <div v-else-if="projects && projects.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in-up">
          <div v-for="project in projects" :key="project.id" class="bg-[#131416]/80 backdrop-blur-md border border-white/5 rounded-3xl overflow-hidden group hover:border-orange-500/30 transition-all duration-300 flex flex-col">

            <div class="h-56 overflow-hidden relative border-b border-white/5">
              <img v-if="project.image_url" :src="project.image_url" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700">
              <div v-else class="w-full h-full bg-[#1A1C1E] flex items-center justify-center text-gray-600">
                <svg class="w-12 h-12 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>

              <div class="absolute top-4 right-4">
                <span class="bg-black/70 backdrop-blur text-orange-400 text-[10px] font-bold px-3 py-1 rounded-full border border-orange-500/20 uppercase tracking-wider">
                  СТОИМОСТЬ: {{ Math.floor(project.cost || 0).toLocaleString() }} ₽
                </span>
              </div>

              <div v-if="isAdmin" class="absolute top-4 left-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button @click.stop="editProject(project)" class="bg-blue-600/90 text-white p-1.5 rounded-lg hover:bg-blue-500"><svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                <button @click.stop="deleteProject(project.id)" class="bg-red-600/90 text-white p-1.5 rounded-lg hover:bg-red-500"><svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
              </div>
            </div>

            <div class="p-6 flex flex-col flex-grow">
              <h3 class="text-xl font-bold text-white mb-2">{{ project.title }}</h3>
              <p class="text-sm text-gray-400 leading-relaxed mb-6 flex-grow line-clamp-4">
                {{ project.description }}
              </p>
              <a :href="project.project_link" target="_blank" class="block w-full text-center py-3 rounded-xl bg-[#1A1C1E] border border-gray-700 text-gray-300 hover:text-white hover:border-orange-500 hover:bg-orange-500/10 transition-all font-medium text-sm">
                Перейти к проекту &rarr;
              </a>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center py-20 text-gray-500">
          <div v-if="pending" class="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
          <p>{{ pending ? 'Загрузка проектов...' : 'Список проектов пуст' }}</p>
        </div>

      </div>
    </main>

    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
        <div class="bg-[#1A1C1E] border border-gray-700 rounded-2xl p-6 w-full max-w-lg relative">
          <h3 class="text-xl font-bold text-white mb-4">{{ isEditing ? 'Редактировать' : 'Добавить' }} проект</h3>
          <form @submit.prevent="submitForm" class="space-y-4">
            <div>
              <label class="block text-xs text-gray-500 mb-1 uppercase">Название</label>
              <input v-model="form.title" type="text" class="auth-input" required>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1 uppercase">Описание</label>
              <textarea v-model="form.description" rows="3" class="auth-input"></textarea>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-gray-500 mb-1 uppercase">Стоимость (₽)</label>
                <input v-model="form.cost" type="number" class="auth-input" required>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1 uppercase">Ссылка</label>
                <input v-model="form.project_link" type="url" class="auth-input" placeholder="https://...">
              </div>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1 uppercase">Картинка</label>
              <input type="file" @change="handleFileChange" accept="image/*" class="text-sm text-gray-400">
            </div>
            <div class="flex gap-3 mt-6">
              <button type="button" @click="showModal = false" class="flex-1 py-3 rounded-xl border border-gray-700 hover:bg-gray-800 transition-colors">Отмена</button>
              <button type="submit" class="flex-1 py-3 rounded-xl bg-orange-600 text-white font-bold hover:bg-orange-500 transition-colors">Сохранить</button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<style scoped>
.auth-input {
  @apply w-full bg-[#131416] border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-orange-500 outline-none transition-colors;
}

.animate-pulse-slow {
  animation: pulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out forwards;
}
</style>